import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-sonner-toast';
import { useAuth } from '@/contexts/AuthContext';
import { reportElement } from '@/lib/firebase/reporting';
import { Loader2 } from 'lucide-react';
import { Element as ElementType } from '@/types';

interface ReportElementModalProps {
  isOpen: boolean;
  onClose: () => void;
  element: ElementType | null;
}

export function ReportElementModal({ isOpen, onClose, element }: ReportElementModalProps) {
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      toast({
        title: 'Sign in required',
        description: 'You need to be signed in to report elements.',
        variant: 'destructive',
      });
      return;
    }

    if (!element) {
      toast({
        title: 'No element selected',
        description: 'Please select an element to report.',
        variant: 'destructive',
      });
      return;
    }

    if (!description.trim()) {
      toast({
        title: 'Description required',
        description: 'Please provide a reason for reporting this element.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await reportElement(
        currentUser.uid,
        currentUser.email || 'unknown',
        element,
        description
      );

      if (result.success) {
        // Show success message, but include warning about email if applicable
        if (result.emailSent) {
          toast({
            title: 'Element Reported',
            description: `Thank you for reporting "${element.name}". We'll review it shortly.`,
          });
        } else {
          toast({
            title: 'Element Reported',
            description: `Your report of "${element.name}" was saved, but we couldn't send an email notification. Our team will still review your report.`,
            variant: 'warning',
          });
        }

        // Log any warnings for debugging
        if (result.warnings.length > 0) {
          console.warn('Report submitted with warnings:', result.warnings);
        }

        // Reset form and close modal
        setDescription('');
        onClose();
      } else {
        // This shouldn't happen with the current implementation, but handle it just in case
        toast({
          title: 'Error',
          description: 'Failed to report element. Please try again later.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error reporting element:', error);
      toast({
        title: 'Error',
        description: 'Failed to report element. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Report Element</DialogTitle>
          <DialogDescription>
            {element ? (
              <>
                You are reporting the element <strong>{element.name}</strong>. Please provide a
                reason for reporting this element.
              </>
            ) : (
              'Please select an element to report.'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {element && (
              <div className="flex items-center justify-center p-4 bg-muted rounded-md">
                <div
                  className="px-3 py-1.5 rounded-xl text-center"
                  style={{
                    backgroundColor: element.color || '#888888',
                    color: '#ffffff',
                  }}
                >
                  {element.name}
                </div>
              </div>
            )}

            <Textarea
              id="description"
              placeholder="Why are you reporting this element? (e.g., inappropriate content, offensive name, etc.)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px]"
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !element}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Report Element'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
